import { useState, useMemo, useEffect, useRef, useCallback, type ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/base/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/base/command";
import { Button } from "@/ui/base/button";
import { Check, ChevronsUpDown, Loader2, Plus, X } from "lucide-react";
import { cn } from "@/nucleo/utilitarios";

export interface SearchableSelectOption {
  /** Unique value */
  value: string;
  /** Primary display label */
  label: string;
  /** Optional secondary label (e.g. code in mono) */
  code?: string;
  /** Optional secondary helper line (e.g. CPF, placa, DRE) */
  description?: string;
  /** Optional flag to mark inactive items (rendered with reduced opacity) */
  inactive?: boolean;
}

interface SearchableSelectBaseProps {
  /** Currently selected value */
  value?: string | null;
  /** Called when selection changes (null = cleared) */
  onChange: (value: string | null) => void;
  /** Trigger placeholder when nothing is selected */
  placeholder?: string;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Empty results message */
  emptyMessage?: string;
  /** Trigger width / extra classes */
  triggerClassName?: string;
  /** Popover content extra classes */
  popoverClassName?: string;
  /** Popover alignment */
  align?: "start" | "center" | "end";
  /** Disabled state */
  disabled?: boolean;
  /** Show loading spinner inside the popover (server mode) */
  loading?: boolean;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Optional ARIA label for the trigger */
  ariaLabel?: string;
  /**
   * Popover modal — precisa ser `true` quando este combobox vive dentro de um
   * `Dialog`. Sem isso, o clique num item da lista falha: o `Dialog` (não
   * modal-aware do popover aberto) trata o pointerdown sobre o item, que está
   * portado direto pra `document.body`, como clique "fora", e fecha/disputa a
   * interação antes do `onSelect` do item disparar. Radix documenta esse modo
   * como a correção pra Popover aninhado em Dialog.
   */
  modal?: boolean;
  /** Optional CTA shown when no result matches the search (e.g. "Cadastrar novo") */
  emptyAction?: {
    label: string;
    onSelect: (search: string) => void;
  };
  /** Render the trigger label for the selected option (default: option.label) */
  renderSelected?: (option: SearchableSelectOption) => ReactNode;
}

interface LocalModeProps extends SearchableSelectBaseProps {
  /** Provide options up-front for client-side filtering */
  options: SearchableSelectOption[];
  onSearchChange?: never;
}

interface ServerModeProps extends SearchableSelectBaseProps {
  /** Options come from a server-side search; component just displays them */
  options: SearchableSelectOption[];
  /** Called (debounced) when the search term changes */
  onSearchChange: (search: string) => void;
  /** Optional debounce in ms (default 300) */
  searchDebounceMs?: number;
}

export type SearchableSelectProps = LocalModeProps | ServerModeProps;

/**
 * SearchableSelect — single-select combobox padrão TEG.
 *
 * Padrão default do sistema (Onda A · T1) — substitui `<Select>` shadcn em qualquer
 * fonte com mais de ~10 itens ou que possa crescer (entidades, parâmetros, filtros).
 *
 * Modos:
 * - **Local**: passa `options` completas, filtra no cliente.
 * - **Server-side**: passa `options` + `onSearchChange` (debounced) — útil para
 *   listas grandes (condutores, monitores, veículos, credenciados, escolas).
 */
export function SearchableSelect(props: SearchableSelectProps) {
  const {
    value,
    onChange,
    options,
    placeholder = "Selecionar...",
    searchPlaceholder = "Digite para buscar...",
    emptyMessage = "Nenhum resultado encontrado.",
    triggerClassName,
    popoverClassName,
    align = "start",
    disabled,
    loading,
    clearable = true,
    ariaLabel,
    emptyAction,
    renderSelected,
    modal = false,
  } = props;

  const isServerMode = "onSearchChange" in props && typeof props.onSearchChange === "function";
  const debounceMs = isServerMode ? (props as ServerModeProps).searchDebounceMs ?? 300 : 0;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset search when popover closes
  useEffect(() => {
    if (!open) {
      setSearch("");
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [open]);

  const handleSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      if (!isServerMode) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        (props as ServerModeProps).onSearchChange(next);
      }, debounceMs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isServerMode, debounceMs]
  );

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) ?? null,
    [options, value]
  );

  // Local-mode filtering (server-mode trusts options as already filtered)
  const visibleOptions = useMemo(() => {
    if (isServerMode) return options;
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((opt) => {
      const haystack = [opt.label, opt.code, opt.description].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search, isServerMode]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)} modal={modal}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel ?? placeholder}
          disabled={disabled}
          className={cn(
            "group flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm ring-offset-background transition-colors",
            "hover:bg-accent/30",
            "focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
            {selectedOption ? (
              renderSelected ? (
                renderSelected(selectedOption)
              ) : (
                <span className="truncate">
                  {selectedOption.code && (
                    <span className="mr-2 font-mono text-xs text-muted-foreground">
                      {selectedOption.code}
                    </span>
                  )}
                  {selectedOption.label}
                </span>
              )
            ) : (
              // `text-muted-foreground` sem opacidade reduzida: a versão a 80%
              // (`/80`) reprova contraste AA — achado pelo axe auditando
              // `/operacao/fiscalizacao/multas`, o primeiro caso deste
              // componente visível já na carga da página (fora de um Dialog
              // fechado). Nativo `<input placeholder>` usa pseudo-elemento
              // `::placeholder`, que o axe não audita do mesmo jeito — por
              // isso esse mesmo padrão em `input.tsx`/`textarea.tsx` nunca
              // apareceu antes; aqui é `<span>` de verdade, span de verdade
              // é auditado.
              <span className="truncate text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {clearable && selectedOption && !disabled && (
              <span
                role="button"
                aria-label="Limpar seleção"
                tabIndex={-1}
                onClick={handleClear}
                className="rounded p-0.5 opacity-50 hover:bg-accent hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-(--radix-popover-trigger-width) min-w-[260px] p-0", popoverClassName)}
        align={align}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Buscando...
              </div>
            ) : (
              <>
                <CommandEmpty>
                  <div className="flex flex-col items-center gap-2 py-4 text-sm text-muted-foreground">
                    <span>{emptyMessage}</span>
                    {emptyAction && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          emptyAction.onSelect(search);
                          setOpen(false);
                        }}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        {emptyAction.label}
                      </Button>
                    )}
                  </div>
                </CommandEmpty>
                <CommandGroup>
                  {visibleOptions.map((opt) => {
                    const isSelected = opt.value === value;
                    return (
                      <CommandItem
                        key={opt.value}
                        value={opt.value}
                        onSelect={() => {
                          onChange(opt.value === value ? null : opt.value);
                          setOpen(false);
                        }}
                        className={cn(
                          "flex items-center gap-2",
                          opt.inactive && "opacity-60"
                        )}
                      >
                        <Check
                          className={cn(
                            "h-4 w-4 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="flex items-center gap-2 truncate">
                            {opt.code && (
                              <span className="font-mono text-xs text-muted-foreground">
                                {opt.code}
                              </span>
                            )}
                            <span className="truncate">{opt.label}</span>
                          </span>
                          {opt.description && (
                            <span className="truncate text-xs text-muted-foreground">
                              {opt.description}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
